/**
 * Cart Drawer Upsell
 * - Loads complementary (fallback: related) recommendations
 * - Renders simple upsell cards with Add (single variant) or Select (multi variant)
 */

class CartDrawerUpsell {
  constructor() {
    this.cartDrawer = document.querySelector('cart-drawer');
    this.loadedProductIds = new Set();
    this.bound = false;
    this.loading = false;
    this.scheduled = false;
    this._loadTimer = null;
    this._observer = null;

    if (!document.getElementById('CartDrawer-UpsellProducts')) return;

    this.init();
  }

  init() {
    this.observeDrawer();
    this.observeDrawerContents();
    this.bindEvents();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.scheduleLoad('cartUpdate');
      });
    }
  }

  bindEvents() {
    if (this.bound) return;
    this.bound = true;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart-upsell-card__add-btn[data-variant-id]');
      if (!btn) return;
      e.preventDefault();
      if (btn.hasAttribute('disabled')) return;
      if (btn.classList.contains('is-loading')) return;
      this.addVariantToCart(btn.dataset.variantId, btn);
    });
  }

  async addVariantToCart(variantId, btn) {
    if (!variantId) return;

    try {
      this.setAddButtonState(btn, 'loading');

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');

      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cart
            .getSectionsToRender()
            .map((s) => s.id)
            .filter(Boolean)
        );
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(btn);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      if (config.headers['Content-Type']) delete config.headers['Content-Type'];
      config.body = formData;

      const res = await fetch(window.routes?.cart_add_url || routes.cart_add_url, config);
      const data = await res.json();

      if (data?.status) {
        // eslint-disable-next-line no-console
        console.error('[Cart Upsell] Add failed:', data);
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: String(variantId),
          cartData: data,
        });
      }

      // Update cart UI (robust fallback if section parsing fails)
      let rendered = false;
      try {
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(data);
          rendered = true;
        }
      } catch (e) {
        console.error('[Cart Upsell] cart.renderContents failed:', e);
        rendered = false;
      }

      if (!rendered) {
        try {
          const sectionIds =
            cart && typeof cart.getSectionsToRender === 'function'
              ? cart
                  .getSectionsToRender()
                  .map((s) => s.id)
                  .filter(Boolean)
              : ['cart-drawer', 'cart-icon-bubble'];
          const root = window.Shopify?.routes?.root || '/';
          const sectionUrl = `${root}?sections=${encodeURIComponent(sectionIds.join(','))}`;
          const sectionRes = await fetch(sectionUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          const sections = await sectionRes.json();
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents({ id: variantId, sections });
          }
        } catch (e2) {
          console.error('[Cart Upsell] fallback render failed:', e2);
        }
      }

      this.setAddButtonState(btn, 'success');
      setTimeout(() => this.setAddButtonState(btn, 'idle'), 1200);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Cart Upsell] Error:', err);
    } finally {
      if (btn.classList.contains('is-loading')) this.setAddButtonState(btn, 'idle');
    }
  }

  setAddButtonState(btn, state) {
    const textEl = btn.querySelector('.cart-upsell-card__add-text');
    const spinnerEl = btn.querySelector('.cart-upsell-card__add-spinner');
    const checkEl = btn.querySelector('.cart-upsell-card__add-check');

    if (state === 'loading') {
      btn.classList.add('is-loading');
      btn.classList.remove('is-success');
      if (spinnerEl) spinnerEl.classList.remove('hidden');
      if (checkEl) checkEl.classList.add('hidden');
      if (textEl) textEl.classList.remove('hidden');
      return;
    }

    if (state === 'success') {
      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      if (spinnerEl) spinnerEl.classList.add('hidden');
      if (checkEl) checkEl.classList.remove('hidden');
      if (textEl) textEl.classList.remove('hidden');
      return;
    }

    // idle
    btn.classList.remove('is-loading');
    btn.classList.remove('is-success');
    if (spinnerEl) spinnerEl.classList.add('hidden');
    if (checkEl) checkEl.classList.add('hidden');
    if (textEl) textEl.classList.remove('hidden');
  }

  observeDrawer() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName !== 'class') return;
        const drawer = mutation.target;
        if (drawer.classList.contains('active')) {
          this.scheduleLoad('drawerOpen');
        }
      });
    });

    const drawerElement = document.querySelector('.drawer');
    if (drawerElement) {
      observer.observe(drawerElement, { attributes: true });
    }
  }

  observeDrawerContents() {
    // When the cart drawer is updated via Sections API, the upsell container resets to the spinner.
    // Scripts inside injected HTML do not execute, so we must detect this DOM replacement and reload.
    const root = document.getElementById('CartDrawer');
    if (!root || typeof MutationObserver === 'undefined') return;

    if (this._observer) this._observer.disconnect();
    this._observer = new MutationObserver(() => {
      const drawerEl = document.querySelector('.drawer');
      const isActive = drawerEl && drawerEl.classList.contains('active');
      if (!isActive) return;

      // Avoid infinite reload loops: our own `showLoading()` inserts the spinner.
      if (this.loading) return;

      const container = document.getElementById('CartDrawer-UpsellProducts');
      if (!container) return;

      // If the spinner is present, the section likely just re-rendered.
      if (container.querySelector('.cart-drawer__upsell-loading')) {
        this.scheduleLoad('drawerReRender');
      }
    });

    this._observer.observe(root, { childList: true, subtree: true });
  }

  scheduleLoad(reason) {
    // Debounce hard to avoid flicker when multiple cart updates happen back-to-back
    // (e.g., add product -> auto-add gift -> drawer re-renders twice).
    if (this._loadTimer) clearTimeout(this._loadTimer);
    const delay = reason === 'drawerOpen' ? 150 : 450;
    this._loadTimer = setTimeout(() => {
      this._loadTimer = null;
      this.loadRecommendations(reason);
    }, delay);
  }

  async loadRecommendations(reason = 'direct') {
    // Re-query every time because cart drawer DOM can be replaced
    this.upsellContainer = document.getElementById('CartDrawer-UpsellProducts');
    this.upsellSection = document.getElementById('CartDrawer-Upsell');
    if (!this.upsellContainer || !this.upsellSection) return;

    // Ensure visible (it may have been hidden previously)
    this.upsellSection.style.display = '';

    if (this.loading) return;
    this.loading = true;

    try {
      // Fetch cart JSON so we can reliably ignore gift lines even when gifts are pinned above products.
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const cartJsonUrl = `${cartUrl}.js`;
      const cartRes = await fetch(cartJsonUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!cartRes.ok) throw new Error('Cart fetch failed');
      const cart = await cartRes.json();
      const items = Array.isArray(cart?.items) ? cart.items : [];

      // Choose the first NON-gift item as the seed product.
      const baseItem = items.find((it) => !(it?.properties && Object.prototype.hasOwnProperty.call(it.properties, '_hg_gift_tier')));
      const productId = baseItem?.product_id;
      if (!productId) {
        this.showEmpty();
        this.loading = false;
        return;
      }

      // Exclude products already in cart (including gifts) from recommendations.
      const cartHandles = new Set(items.map((it) => it && it.handle).filter(Boolean));

      this.showLoading();

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const rootPath = root.endsWith('/') ? root : `${root}/`;

      const fetchRecs = async (intent) => {
        const url = `${rootPath}recommendations/products.json?product_id=${productId}&limit=6&intent=${intent}`;
        const res = await fetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        return data.products || [];
      };

      let products = await fetchRecs('complementary');
      if (products.length === 0) products = await fetchRecs('related');

      // Exclude products already in cart
      products = products.filter((p) => !cartHandles.has(p.handle));
      products = products.slice(0, 4);

      if (products.length === 0) {
        this.showEmpty();
        this.loading = false;
        return;
      }

      this.renderProducts(products);
    } catch (err) {
      console.error('Failed to load upsell recommendations:', err);
      this.showEmpty();
    } finally {
      this.loading = false;
    }
  }

  renderProducts(products) {
    if (!this.upsellContainer) return;
    this.upsellContainer.innerHTML = products.map((p) => this.renderProductCard(p)).join('');
  }

  renderProductCard(product) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const rootPath = root.endsWith('/') ? root : `${root}/`;
    const productUrl = `${rootPath}products/${product.handle}`;

    const firstAvailableVariant = product.variants.find((v) => v.available) || product.variants[0];
    const hasVariants = product.variants.length > 1 && !product.variants.every((v) => v.title === 'Default Title');
    const isOnSale = product.compare_at_price && product.compare_at_price > product.price;

    const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);

    const imageUrl = product.featured_image ? product.featured_image.replace(/\.([^.]+)$/, '_200x.$1') : '';

    const truncateTitle = (title, length = 30) => (title.length > length ? title.substring(0, length) + '...' : title);

    const actionHtml = (() => {
      if (!firstAvailableVariant) {
        return `<button type="button" class="cart-upsell-card__add-btn button button--secondary" disabled>Sold out</button>`;
      }

      // Multi-variant => go to product page (avoid broken quick-add)
      if (hasVariants) {
        return `<a href="${productUrl}" class="cart-upsell-card__add-btn button button--secondary"><span>Select</span></a>`;
      }

      // Single variant => add directly
      if (!firstAvailableVariant.available) {
        return `<button type="button" class="cart-upsell-card__add-btn button button--secondary" disabled><span>Sold out</span></button>`;
      }

      return `
        <button type="button" class="cart-upsell-card__add-btn button button--secondary" data-variant-id="${firstAvailableVariant.id}" aria-label="Add to cart: ${product.title}">
          <span class="cart-upsell-card__add-text">Add</span>
          <span class="cart-upsell-card__add-spinner loading__spinner hidden">
            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </span>
          <span class="cart-upsell-card__add-check hidden">
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </span>
        </button>
      `;
    })();

    return `
      <div class="cart-upsell-card" data-product-id="${product.id}">
        <a href="${productUrl}" class="cart-upsell-card__image-wrapper" tabindex="-1" aria-hidden="true">
          ${imageUrl
            ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy" class="cart-upsell-card__image">`
            : `<div class="cart-upsell-card__placeholder"><svg viewBox="0 0 20 20"><path d="M10 0C4.5 0 0 4.5 0 10s4.5 10 10 10 10-4.5 10-10S15.5 0 10 0zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z"/></svg></div>`}
        </a>
        <div class="cart-upsell-card__info">
          <a href="${productUrl}" class="cart-upsell-card__title h5">${truncateTitle(product.title)}</a>
          <div class="cart-upsell-card__price">
            ${isOnSale
              ? `<span class="cart-upsell-card__price--sale">${formatMoney(product.price)}</span>
                 <s class="cart-upsell-card__price--compare">${formatMoney(product.compare_at_price)}</s>`
              : `<span>${formatMoney(product.price)}</span>`}
          </div>
        </div>
        <div class="cart-upsell-card__action">
          ${actionHtml}
        </div>
      </div>
    `;
  }

  showLoading() {
    this.upsellContainer = document.getElementById('CartDrawer-UpsellProducts');
    this.upsellSection = document.getElementById('CartDrawer-Upsell');
    if (!this.upsellContainer || !this.upsellSection) return;
    this.upsellSection.style.display = '';

    this.upsellContainer.innerHTML = `
      <div class="cart-drawer__upsell-loading">
        <span class="loading__spinner">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </span>
      </div>
    `;
  }

  showEmpty() {
    this.upsellSection = document.getElementById('CartDrawer-Upsell');
    if (this.upsellSection) {
      this.upsellSection.style.display = 'none';
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartDrawerUpsell());
} else {
  new CartDrawerUpsell();
}
